import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Smartphone, Mail, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/App";
import { Logo } from "@/components/Logo";

const inp =
  "mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 focus:border-[#4F46E5]";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [tab, setTab] = useState("phone"); // "phone" | "email"
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null); // { channel, delivered, provider }
  const [demoOtp, setDemoOtp] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  // Show a friendly banner when the user was bounced here from an expired
  // session (see api.js response interceptor).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "expired") {
      toast.info("Your session expired — please log in again");
    }
  }, []);

  const identifier = tab === "phone" ? phone.trim() : email.trim();

  const validate = () => {
    if (tab === "phone") {
      const p = phone.replace(/\s|-/g, "");
      if (!/^(\+?\d{10,15})$/.test(p)) {
        toast.error("Enter a valid phone number (10 digits or E.164 format)");
        return false;
      }
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error("Enter a valid email address");
        return false;
      }
    }
    return true;
  };

  const requestOtp = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ota/auth/request-otp", { identifier });
      setSent({ channel: data.channel, delivered: data.delivered, provider: data.provider });
      setDemoOtp(data.demo_otp || null);
      if (data.delivered) {
        toast.success(
          data.channel === "sms"
            ? `OTP sent to ${identifier} — check your SMS`
            : `OTP sent to ${identifier} — check your inbox`
        );
      } else {
        toast.success("Demo OTP shown below — provider not configured in this environment");
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ota/auth/verify-otp", { identifier, otp });
      login(data.token, data.user);
      toast.success(`Welcome, ${data.user.name || "Admin"}`);
      // If we were redirected here from a protected page, return the user
      // back to it after a successful re-login.
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");
      nav(next && next.startsWith("/") ? next : "/pms");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const changeIdentifier = () => {
    setSent(null);
    setDemoOtp(null);
    setOtp("");
  };

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      type="button"
      data-testid={`login-tab-${id}`}
      onClick={() => { setTab(id); }}
      className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-all ${
        tab === id
          ? "bg-white text-[#4F46E5] shadow-sm border border-slate-200"
          : "text-slate-500 hover:text-slate-800"
      }`}
    >
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 reveal">
          <a href="/" className="inline-block"><Logo h={48} /></a>
          <p className="text-sm text-slate-500 mt-2">
            One login for the Atiti app, Host dashboard, and PMS — sign in with either your phone or email.
          </p>
        </div>

        <div
          className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200 p-8 reveal"
          style={{ animationDelay: "0.1s" }}
        >
          {!sent ? (
            <div className="space-y-5">
              <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                <TabBtn id="phone" icon={Smartphone} label="Phone" />
                <TabBtn id="email" icon={Mail} label="Email" />
              </div>

              {tab === "phone" ? (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Mobile number
                  </label>
                  <input
                    data-testid="login-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className={inp}
                    autoFocus
                  />
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    We'll text you a 6-digit code via SMS.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Email address
                  </label>
                  <input
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@atiti.space"
                    type="email"
                    className={inp}
                    autoFocus
                  />
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    We'll email you a 6-digit code.
                  </p>
                </div>
              )}

              <button
                data-testid="login-request-otp-button"
                onClick={requestOtp}
                disabled={busy || !identifier}
                className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader2 className="animate-spin" size={16} /> : (tab === "phone" ? <Smartphone size={16} /> : <Mail size={16} />)}
                Send OTP
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center text-sm text-slate-600">
                Code sent to <b className="text-slate-900">{identifier}</b>
                <button onClick={changeIdentifier} className="block mx-auto mt-1 text-[11px] text-[#4F46E5] hover:underline">
                  Change {tab === "phone" ? "phone" : "email"}
                </button>
              </div>

              {demoOtp && (
                <div
                  data-testid="demo-otp-banner"
                  className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-4 text-center"
                >
                  <p className="text-xs text-slate-500 mb-1">
                    DEMO MODE — provider not configured in this environment
                  </p>
                  <p className="font-mono text-2xl font-semibold tracking-[0.4em] brand-pink">{demoOtp}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Enter OTP
                </label>
                <input
                  data-testid="login-otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  maxLength={6}
                  placeholder="6-digit code"
                  autoFocus
                  className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-center font-mono text-lg tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 focus:border-[#4F46E5]"
                />
              </div>

              <button
                data-testid="login-verify-otp-button"
                onClick={verify}
                disabled={busy || otp.length !== 6}
                className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
                Verify &amp; log in
              </button>

              <button
                onClick={requestOtp}
                disabled={busy}
                data-testid="login-resend-otp-button"
                className="w-full text-xs text-slate-400 hover:text-slate-600"
              >
                Didn't get it? Resend
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Same identity as the Atiti OTA — one account, two dashboards.
        </p>
      </div>
    </div>
  );
}
